// src/pages/Performance.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../api";
import PremiumUpsellBanner from "../components/PremiumUpsellBanner";
import SiteFooterDisclaimer from "../components/SiteFooterDisclaimer";
import { prettyCompetitionName } from "../utils/competitionNames";

const COL = {
  bg: "#0a120d",
  panel: "#111915",
  panelSoft: "#0f1512",
  border: "rgba(194, 255, 214, 0.14)",
  text: "#eaf4ed",
  muted: "rgba(234, 244, 237, 0.68)",
  good: "#34d26a",
  bad: "#ff6b6b",
  accent: "#39c778",
  warn: "#f2c14e",
};

const PICK_SCOPES = [
  { id: "all", label: "All Picks" },
  { id: "premium", label: "Premium Only" },
];
const PERFORMANCE_AREAS = [
  { id: "picks", label: "Single Picks" },
  { id: "accas", label: "Accas" },
];

const prettyComp = (c) => prettyCompetitionName(c, "Football");
const fmt = (n, d = 1) => (Number.isFinite(Number(n)) ? Number(n).toFixed(d) : "0.0");

const toMonthSpan = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

const getCurrentMonthSpan = () => toMonthSpan(new Date());

const buildMonthSpans = (count = 5) => {
  const base = new Date();
  base.setDate(1);

  const months = Array.from({ length: count }, (_, idx) => {
    const date = new Date(base);
    date.setMonth(base.getMonth() - idx);
    return {
      id: toMonthSpan(date),
      label: date.toLocaleDateString("en-GB", { month: "short" }),
    };
  });

  return [...months, { id: "all", label: "All" }];
};

const getSpanLabel = (span, spanOptions) => {
  if (span === "all") return "All time";
  const optionLabel = spanOptions.find((option) => option.id === span)?.label;
  const [year, month] = String(span).split("-");
  if (!year || !month) return optionLabel || span;
  const date = new Date(Number(year), Number(month) - 1, 1);
  const fullLabel = date.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  return optionLabel ? fullLabel : span;
};

const getPickDateValue = (pick = {}) => pick.settled_at || pick.created_at || pick.date || "";

export default function Performance() {
  const spanOptions = useMemo(() => buildMonthSpans(), []);
  const [span, setSpan] = useState(getCurrentMonthSpan);
  const [pickScope, setPickScope] = useState("all");
  const [area, setArea] = useState("picks");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const request = area === "accas"
      ? api.get("/api/public/accas/record", { params: { span } })
      : api.get("/api/public/picks/record", { params: { span, pick_scope: pickScope } });
    request
      .then(({ data: payload }) => {
        if (alive) setData(payload);
      })
      .catch(() => alive && setData(null))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [span, pickScope, area]);

  const picks = useMemo(() => {
    if (area === "accas") {
      if (!Array.isArray(data?.accas)) return [];
      return data.accas.map((ticket) => ({
        ...ticket,
        id: ticket.id,
        market: ticket.title || `${ticket.legs_count || 0}-leg acca`,
        league: ticket.sport || "Acca",
        bookmaker: "Combined",
        price: ticket.combined_price,
        stake: ticket.stake_units,
        units: ticket.profit,
        created_at: ticket.settled_at || ticket.day,
      })).filter((ticket) => ["won", "lost", "void"].includes(String(ticket.result || "").toLowerCase()));
    }
    if (!Array.isArray(data?.picks)) return [];
    return data.picks
      .filter((p) => {
        const res = (p.result || "").toLowerCase();
        return res === "won" || res === "lost" || res === "void";
      })
      .sort((a, b) => {
        const aDate = getPickDateValue(a);
        const bDate = getPickDateValue(b);
        const da = aDate ? new Date(aDate).getTime() : a.id || 0;
        const db = bDate ? new Date(bDate).getTime() : b.id || 0;
        return da - db;
      });
  }, [data?.picks, data?.accas, area]);

  const kpis = useMemo(() => {
    let oddsSum = 0;

    const decisions = picks.filter((p) => {
      const result = String(p?.result || "").toLowerCase();
      return result === "won" || result === "lost";
    });

    decisions.forEach((p) => {
      const price = +p.price || 0;
      oddsSum += price;
    });

    const summary = data?.summary || {};
    const record = summary.record || {};
    const won = Number(record.won || 0);
    const lost = Number(record.lost || 0);
    const voided = Number(record.void || 0);
    const hitRate = won + lost > 0 ? (won / (won + lost)) * 100 : 0;
    const avgOdds = decisions.length ? oddsSum / decisions.length : 0;

    return {
      staked: Number(summary.staked || 0),
      returned: Number(summary.returned || 0),
      pnl: Number(summary.pnl || 0),
      roi: Number(summary.roi || 0),
      avgOdds,
      hitRate,
      record: { won, lost, void: voided },
      count: won + lost,
    };
  }, [data?.summary, picks]);

  const equity = useMemo(() => {
    if (!picks.length) return { path: "", points: [], values: [], labels: [], min: 0, max: 0, last: 0 };

    let running = 0;
    const values = picks.map((p) => {
      const res = (p.result || "").toLowerCase();
      const stake = Number(p.stake || 0);
      const price = Number(p.price || 0);
      if (res === "won") running += stake * (price - 1);
      else if (res === "lost") running -= stake;
      return running;
    });

    const labels = picks.map((p) => {
      const dateValue = getPickDateValue(p);
      if (!dateValue) return `#${p.id || "pick"}`;
      return new Date(dateValue).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
    });

    const min = Math.min(...values);
    const max = Math.max(...values);
    const spanVal = max - min || 1;
    const h = 100;
    const w = 100;
    const pad = 5;

    const points = values.map((v, i) => {
      const x = values.length === 1 ? 50 : (i / (values.length - 1)) * (w - pad * 2) + pad;
      const y = h - pad - ((v - min) / spanVal) * (h - pad * 2);
      return { x, y };
    });

    const path = points.map((p, i) => `${i === 0 ? "M" : "L"}${p.x},${p.y}`).join(" ");
    return { path, points, values, labels, min, max, last: values[values.length - 1] };
  }, [picks]);

  const byDimension = useCallback((keyName, labelMap) => {
    const map = {};
    for (const p of picks) {
      const key = p[keyName] || "—";
      if (!map[key]) {
        map[key] = { key, picks: 0, won: 0, lost: 0, void: 0, staked: 0, returned: 0 };
      }
      const row = map[key];
      const stake = Number(p.stake || 1);
      const price = Number(p.price || 0);
      const res = (p.result || "").toLowerCase();

      if (res === "won" || res === "win") {
        row.picks += 1;
        row.staked += stake;
        row.won += 1;
        row.returned += stake * price;
      } else if (res === "lost" || res === "lose") {
        row.picks += 1;
        row.staked += stake;
        row.lost += 1;
      } else {
        row.void += 1;
      }
    }

    return Object.values(map)
      .map((r) => {
        const pnl = r.returned - r.staked;
        const roi = r.staked ? (pnl / r.staked) * 100 : 0;
        return {
          ...r,
          label: typeof labelMap === "function" ? labelMap(r.key) : r.key,
          pnl,
          roi,
        };
      })
      .sort((a, b) => b.roi - a.roi);
  }, [picks]);

  const byMarket = useMemo(() => byDimension("market"), [byDimension]);
  const byLeague = useMemo(() => byDimension("league", prettyComp), [byDimension]);
  const byBook = useMemo(() => byDimension("bookmaker"), [byDimension]);

  const bestMarket = byMarket[0];
  const worstMarket = byMarket.length ? [...byMarket].sort((a, b) => a.roi - b.roi)[0] : null;
  const isPremiumScope = pickScope === "premium";
  const scopeTitleLabel = area === "accas" ? "Published Accas" : isPremiumScope ? "Premium Featured Picks" : "All Featured Picks";
  const spanTitleLabel = getSpanLabel(span, spanOptions);
  const emptyStateText = area === "accas"
    ? `No published accas settled in ${spanTitleLabel}.`
    : isPremiumScope
    ? `No premium featured picks settled in ${spanTitleLabel}.`
    : `No settled featured picks in ${spanTitleLabel}.`;
  const money = (value) => area === "accas" ? `${fmt(value, 2)}u` : `£${fmt(value, 2)}`;

  return (
    <div style={{ maxWidth: 1120, margin: "0 auto", padding: 16, color: COL.text }}>
      <div
        style={{
          border: `1px solid ${COL.border}`,
          borderRadius: 16,
          padding: 16,
          background: `linear-gradient(135deg, ${COL.panel} 0%, ${COL.panelSoft} 100%)`,
          marginBottom: 14,
        }}
      >
        <h1 style={{ margin: 0, marginBottom: 6, fontSize: 30 }}>Performance</h1>
        <p style={{ margin: 0, color: COL.muted }}>
          {area === "accas"
            ? "A dedicated accumulator record with ticket-level ROI, units and settlement history."
            : "Live analytics for featured settled picks, grouped by calendar month with ROI and P/L breakdowns."}
        </p>
        <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <span
            style={{
              fontSize: 12,
              color: COL.text,
              border: `1px solid ${COL.border}`,
              borderRadius: 999,
              padding: "4px 10px",
              background: "rgba(255,255,255,.03)",
            }}
          >
            Performance ({scopeTitleLabel} • {spanTitleLabel})
          </span>
        </div>
      </div>

      <PremiumUpsellBanner
        mode="link"
        message="Unlock deeper history, advanced filters and richer performance intelligence with CSB Premium."
      />

      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 8, marginBottom: 16, padding: 4, border: `1px solid ${COL.border}`, borderRadius: 14, background: COL.panel }}>
        {PERFORMANCE_AREAS.map((option) => {
          const active = area === option.id;
          return (
            <button key={option.id} onClick={() => setArea(option.id)} style={{ padding: "11px 14px", borderRadius: 10, border: 0, cursor: "pointer", fontWeight: 800, background: active ? COL.accent : "transparent", color: active ? COL.bg : COL.muted }}>
              {option.label}
            </button>
          );
        })}
      </div>

      {area === "picks" ? <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        {spanOptions.map((s) => {
          const active = span === s.id;
          return (
            <button
              key={s.id}
              onClick={() => setSpan(s.id)}
              style={{
                padding: "7px 12px",
                borderRadius: 999,
                border: `1px solid ${active ? COL.accent : COL.border}`,
                background: active ? "rgba(57,199,120,.2)" : "rgba(255,255,255,.03)",
                color: COL.text,
                cursor: "pointer",
              }}
            >
              {s.label}
            </button>
          );
        })}
      </div> : null}

      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        {PICK_SCOPES.map((s) => {
          const active = pickScope === s.id;
          return (
            <button
              key={s.id}
              onClick={() => setPickScope(s.id)}
              style={{
                padding: "7px 12px",
                borderRadius: 999,
                border: `1px solid ${active ? COL.accent : COL.border}`,
                background: active ? "rgba(57,199,120,.2)" : "rgba(255,255,255,.03)",
                color: COL.text,
                cursor: "pointer",
                fontWeight: active ? 600 : 500,
              }}
            >
              {s.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div style={{ color: COL.muted }}>Loading performance…</div>
      ) : (
        <>
          {!picks.length && (
            <div
              style={{
                border: `1px solid ${COL.border}`,
                borderRadius: 14,
                padding: 14,
                marginBottom: 16,
                background: "rgba(255,255,255,.03)",
                color: COL.text,
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 4 }}>No settled {area === "accas" ? "accas" : "picks"} for this view</div>
              <div style={{ color: COL.muted, fontSize: 13 }}>
                {emptyStateText}
              </div>
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 10, marginBottom: 16 }}>
            <Kpi title={area === "accas" ? "Graded Accas" : "Graded Picks"} value={kpis.count} />
            <Kpi title="Record" value={`${kpis.record.won}W-${kpis.record.lost}L-${kpis.record.void}V`} />
            <Kpi title="Hit Rate" value={`${fmt(kpis.hitRate)}%`} tone={kpis.hitRate >= 50 ? "good" : "neutral"} />
            <Kpi title="Avg Odds" value={fmt(kpis.avgOdds, 2)} />
            <Kpi title="Staked" value={money(kpis.staked)} />
            <Kpi title="Returned" value={money(kpis.returned)} />
            <Kpi title="P/L" value={money(kpis.pnl)} tone={kpis.pnl > 0 ? "good" : kpis.pnl < 0 ? "bad" : "neutral"} />
            <Kpi title="ROI" value={`${fmt(kpis.roi)}%`} tone={kpis.roi > 0 ? "good" : kpis.roi < 0 ? "bad" : "neutral"} />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1.35fr .65fr", gap: 12, marginBottom: 16 }}>
            <Panel title="Equity Curve (P/L)">
              {equity.points.length > 1 ? (
                <>
                  <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ width: "100%", height: 180, background: "rgba(0,0,0,.35)", borderRadius: 10 }}>
                    <line x1="0" x2="100" y1="95" y2="95" stroke="rgba(255,255,255,.08)" strokeWidth="0.4" />
                    <path d={equity.path} fill="none" stroke={equity.last >= 0 ? COL.good : COL.bad} strokeWidth="1.4" />
                  </svg>
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, fontSize: 12, color: COL.muted }}>
                    <span>Min: {money(equity.min)}</span>
                    <span>Latest: {money(equity.last)}</span>
                    <span>Max: {money(equity.max)}</span>
                  </div>
                </>
              ) : (
                <div style={{ color: COL.muted }}>Not enough settled picks to draw equity yet.</div>
              )}
            </Panel>

            {area === "picks" ? <Panel title="Highlights">
              <div style={{ display: "grid", gap: 8 }}>
                <Highlight label="Best Market" value={bestMarket ? `${bestMarket.label} (${fmt(bestMarket.roi)}%)` : "—"} positive />
                <Highlight label="Worst Market" value={worstMarket ? `${worstMarket.label} (${fmt(worstMarket.roi)}%)` : "—"} negative />
                <Highlight label="Top League" value={byLeague[0] ? `${byLeague[0].label} (${fmt(byLeague[0].roi)}%)` : "—"} />
                <Highlight label="Top Bookmaker" value={byBook[0] ? `${byBook[0].label} (${fmt(byBook[0].roi)}%)` : "—"} />
              </div>
            </Panel> : <Panel title="Acca Snapshot">
              <div style={{ display: "grid", gap: 8 }}>
                <Highlight label="Settled Tickets" value={kpis.count} />
                <Highlight label="Average Combined Odds" value={fmt(kpis.avgOdds, 2)} />
                <Highlight label="Current P/L" value={`${kpis.pnl >= 0 ? "+" : ""}${fmt(kpis.pnl, 2)}u`} positive={kpis.pnl > 0} negative={kpis.pnl < 0} />
              </div>
            </Panel>}
          </div>

          {area === "picks" ? <>
          <Panel title="By Market">
            <BreakdownTable rows={byMarket} headLabel="Market" />
          </Panel>

          <Panel title="By League / Competition">
            <BreakdownTable rows={byLeague} headLabel="League" />
          </Panel>

          <Panel title="By Bookmaker">
            <BreakdownTable rows={byBook} headLabel="Bookmaker" />
          </Panel>
          </> : <Panel title="Recent Acca Results">
            <AccaResultsTable rows={Array.isArray(data?.accas) ? data.accas : []} />
          </Panel>}
        </>
      )}

      <SiteFooterDisclaimer variant="short" />
    </div>
  );
}

function Kpi({ title, value, tone = "neutral" }) {
  const color = tone === "good" ? COL.good : tone === "bad" ? COL.bad : COL.text;
  const bg = tone === "good" ? "rgba(52,210,106,.12)" : tone === "bad" ? "rgba(255,107,107,.12)" : "rgba(255,255,255,.03)";
  return (
    <div style={{ background: bg, border: `1px solid ${COL.border}`, borderRadius: 12, padding: "10px 12px" }}>
      <div style={{ fontSize: 12, color: COL.muted }}>{title}</div>
      <div style={{ marginTop: 3, fontSize: 20, fontWeight: 700, color }}>{value}</div>
    </div>
  );
}

function Panel({ title, children }) {
  return (
    <div
      style={{
        background: COL.panel,
        border: `1px solid ${COL.border}`,
        borderRadius: 14,
        padding: 12,
        marginBottom: 14,
        overflowX: "auto",
      }}
    >
      <h3 style={{ margin: 0, marginBottom: 10, fontSize: 18 }}>{title}</h3>
      {children}
    </div>
  );
}

function Highlight({ label, value, positive = false, negative = false }) {
  const color = positive ? COL.good : negative ? COL.bad : COL.text;
  return (
    <div style={{ border: `1px solid ${COL.border}`, borderRadius: 10, background: "rgba(255,255,255,.03)", padding: "8px 10px" }}>
      <div style={{ fontSize: 12, color: COL.muted }}>{label}</div>
      <div style={{ marginTop: 2, fontSize: 14, color, fontWeight: 600 }}>{value}</div>
    </div>
  );
}

function BreakdownTable({ rows, headLabel }) {
  if (!rows.length) return <div style={{ color: COL.muted }}>No settled picks for this period and scope.</div>;

  return (
    <table style={{ width: "100%", borderCollapse: "collapse" }}>
      <thead>
        <tr style={{ background: "rgba(255,255,255,.05)" }}>
          <Th>{headLabel}</Th>
          <Th align="right">Picks</Th>
          <Th align="right">Record</Th>
          <Th align="right">P/L</Th>
          <Th align="right">ROI</Th>
          <Th>ROI bar</Th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.key} style={{ borderTop: `1px solid ${COL.border}` }}>
            <Td>{r.label}</Td>
            <Td align="right">{r.picks}</Td>
            <Td align="right">{r.won}W-{r.lost}L-{r.void}V</Td>
            <Td align="right" style={{ color: r.pnl >= 0 ? COL.good : COL.bad }}>£{fmt(r.pnl, 2)}</Td>
            <Td align="right" style={{ color: r.roi >= 0 ? COL.good : COL.bad }}>{fmt(r.roi)}%</Td>
            <Td><Bar value={r.roi} /></Td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function AccaResultsTable({ rows }) {
  if (!rows.length) return <div style={{ color: COL.muted }}>No published accas in this period.</div>;
  return (
    <table style={{ width: "100%", borderCollapse: "collapse" }}>
      <thead><tr style={{ background: "rgba(255,255,255,.05)" }}>
        <Th>Date</Th><Th>Acca</Th><Th align="right">Legs</Th><Th align="right">Odds</Th><Th align="right">Result</Th><Th align="right">P/L</Th>
      </tr></thead>
      <tbody>{rows.map((ticket) => {
        const result = String(ticket.result || "PENDING").toUpperCase();
        const profit = Number(ticket.profit || 0);
        return <tr key={ticket.id} style={{ borderTop: `1px solid ${COL.border}` }}>
          <Td>{ticket.day}</Td>
          <Td>{ticket.title || `Acca #${ticket.id}`}</Td>
          <Td align="right">{ticket.legs_count || 0}</Td>
          <Td align="right">{fmt(ticket.combined_price, 2)}x</Td>
          <Td align="right" style={{ color: result === "WON" ? COL.good : result === "LOST" ? COL.bad : COL.muted }}>{result}</Td>
          <Td align="right" style={{ color: profit > 0 ? COL.good : profit < 0 ? COL.bad : COL.muted }}>{ticket.profit == null ? "—" : `${profit >= 0 ? "+" : ""}${fmt(profit, 2)}u`}</Td>
        </tr>;
      })}</tbody>
    </table>
  );
}

function Th({ children, align = "left" }) {
  return (
    <th style={{ textAlign: align, padding: "8px 6px", fontSize: 12, color: COL.muted, whiteSpace: "nowrap" }}>
      {children}
    </th>
  );
}

function Td({ children, align = "left", style = {} }) {
  return (
    <td style={{ textAlign: align, padding: "8px 6px", fontSize: 13, ...style }}>
      {children}
    </td>
  );
}

function Bar({ value }) {
  const pct = Math.max(0, Math.min(Math.abs(value), 100));
  const isNeg = value < 0;
  return (
    <div style={{ height: 10, background: "rgba(255,255,255,.08)", borderRadius: 999, overflow: "hidden", minWidth: 80 }}>
      <div style={{ width: `${pct}%`, height: "100%", background: isNeg ? COL.bad : COL.good, transition: "width .25s ease-out" }} />
    </div>
  );
}
